import type {
  McpGatewayFieldSpec,
  McpGatewayPluginListing,
  McpGatewayStatus,
} from "@t3tools/contracts";
import { CheckCircle2Icon, ChevronDownIcon, CircleIcon, XCircleIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  type AtomCommandResult,
  isAtomCommandInterrupted,
  squashAtomCommandFailure,
} from "@t3tools/client-runtime/state/runtime";

import { usePrimarySettings } from "~/hooks/useSettings";
import { readLocalApi } from "~/localApi";
import { usePrimaryEnvironmentId } from "~/state/environments";
import { useEnvironmentQuery } from "~/state/query";
import { serverEnvironment } from "~/state/server";
import { useAtomCommand } from "~/state/use-atom-command";
import { Button } from "../ui/button";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "../ui/collapsible";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { IntegrationIcon } from "./integrationIcons";
import { SettingsPageContainer, SettingsRow, SettingsSection } from "./settingsLayout";

// Settings → Integrations: connect the self-hosted MCP gateway
// (infra/mcp-gateway/, deployed via Coolify) and activate per-service plugins
// (Notion, Slack, …). Plugin forms are rendered generically from the field
// specs the server sends, so new plugins need no web changes.

function HealthBadge({ status }: { status: McpGatewayStatus | null }) {
  if (!status || !status.configured) {
    return (
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        <CircleIcon className="size-3" />
        Not configured
      </span>
    );
  }
  return status.healthy ? (
    <span className="inline-flex items-center gap-1 text-emerald-600">
      <CheckCircle2Icon className="size-3" />
      Gateway reachable
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-destructive">
      <XCircleIcon className="size-3" />
      {status.lastError ? `Unreachable: ${status.lastError}` : "Gateway unreachable"}
    </span>
  );
}

function FieldInput({
  field,
  value,
  placeholderOverride,
  onChange,
}: {
  field: McpGatewayFieldSpec;
  value: string;
  placeholderOverride?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="grid gap-1.5 text-xs font-medium text-muted-foreground">
      <span>
        {field.label}
        {field.optional ? <span className="text-muted-foreground/60"> (optional)</span> : null}
      </span>
      <Input
        type={field.control === "password" ? "password" : "text"}
        value={value}
        placeholder={placeholderOverride ?? field.placeholder ?? ""}
        autoComplete="off"
        onChange={(event) => onChange(event.target.value)}
      />
      {field.helpText ? (
        <span className="font-normal text-[11px] text-muted-foreground/70">{field.helpText}</span>
      ) : null}
    </label>
  );
}

function GatewayConnectionSection({
  status,
  onChanged,
}: {
  status: McpGatewayStatus | null;
  onChanged: () => void;
}) {
  const persisted = usePrimarySettings((settings) => settings.mcpGateway);
  const setConnection = useAtomCommand(
    serverEnvironment.mcpGatewaySetConnection,
    "mcp gateway set connection",
  );
  const environmentId = usePrimaryEnvironmentId();
  const [url, setUrl] = useState(persisted.url);
  const [adminUrl, setAdminUrl] = useState(persisted.adminUrl);
  const [gatewayToken, setGatewayToken] = useState("");
  const [adminToken, setAdminToken] = useState("");
  const [saveState, setSaveState] = useState<
    { readonly status: "idle" | "saving" } | { readonly status: "error"; readonly message: string }
  >({ status: "idle" });

  // Follow settings loaded/changed elsewhere as long as the drafts are clean.
  useEffect(() => {
    setUrl((current) => (current === "" ? persisted.url : current));
    setAdminUrl((current) => (current === "" ? persisted.adminUrl : current));
  }, [persisted.url, persisted.adminUrl]);

  const commitConnection = useCallback(
    async (enabled: boolean) => {
      if (environmentId === null) return;
      setSaveState({ status: "saving" });
      const result = await setConnection({
        environmentId,
        input: {
          url: url.trim(),
          adminUrl: adminUrl.trim(),
          enabled,
          ...(gatewayToken.trim() ? { gatewayToken: gatewayToken.trim() } : {}),
          ...(adminToken.trim() ? { adminToken: adminToken.trim() } : {}),
        },
      });
      if (result._tag === "Success" || isAtomCommandInterrupted(result)) {
        setGatewayToken("");
        setAdminToken("");
        setSaveState({ status: "idle" });
      } else {
        const error = squashAtomCommandFailure(result);
        setSaveState({
          status: "error",
          message: error instanceof Error ? error.message : "Saving the connection failed.",
        });
      }
      onChanged();
    },
    [adminToken, adminUrl, environmentId, gatewayToken, onChanged, setConnection, url],
  );

  return (
    <SettingsSection title="Gateway connection">
      <SettingsRow
        title="Self-hosted MCP gateway"
        description={
          <>
            Deploy the stack in <code className="text-[11px]">infra/mcp-gateway/</code> (see its
            README for Coolify setup), then connect it here. Agents get every activated integration
            through this endpoint.
          </>
        }
        status={
          saveState.status === "error" ? (
            <span className="inline-flex items-center gap-1 text-destructive">
              <XCircleIcon className="size-3" />
              {saveState.message}
            </span>
          ) : (
            <HealthBadge status={status} />
          )
        }
        control={
          <>
            <Switch
              checked={persisted.enabled}
              onCheckedChange={(enabled) => void commitConnection(enabled)}
              aria-label="Inject gateway into agent sessions"
            />
            <Button
              size="xs"
              variant="outline"
              disabled={saveState.status === "saving"}
              onClick={() => void commitConnection(persisted.enabled)}
            >
              {saveState.status === "saving" ? "Saving" : "Save & Test"}
            </Button>
          </>
        }
      >
        <div className="grid gap-3 border-t border-border/60 py-3.5 sm:grid-cols-2">
          <label className="grid gap-1.5 text-xs font-medium text-muted-foreground">
            Gateway URL
            <Input
              value={url}
              placeholder="https://mcp.example.com"
              onChange={(event) => setUrl(event.target.value)}
            />
          </label>
          <label className="grid gap-1.5 text-xs font-medium text-muted-foreground">
            Admin URL
            <Input
              value={adminUrl}
              placeholder="https://mcp-admin.example.com"
              onChange={(event) => setAdminUrl(event.target.value)}
            />
          </label>
          <label className="grid gap-1.5 text-xs font-medium text-muted-foreground">
            Gateway token
            <Input
              type="password"
              value={gatewayToken}
              autoComplete="off"
              placeholder={status?.hasGatewayToken ? "Configured — enter to replace" : "Required"}
              onChange={(event) => setGatewayToken(event.target.value)}
            />
          </label>
          <label className="grid gap-1.5 text-xs font-medium text-muted-foreground">
            Admin token
            <Input
              type="password"
              value={adminToken}
              autoComplete="off"
              placeholder={status?.hasAdminToken ? "Configured — enter to replace" : "Required"}
              onChange={(event) => setAdminToken(event.target.value)}
            />
          </label>
        </div>
      </SettingsRow>
    </SettingsSection>
  );
}

/** UI poll cadence while waiting for the user to finish browser consent. */
const OAUTH_POLL_INTERVAL_MS = 2_000;
const OAUTH_POLL_TIMEOUT_MS = 10 * 60_000;

function PluginRow({
  listing,
  gatewayConfigured,
  onChanged,
}: {
  listing: McpGatewayPluginListing;
  gatewayConfigured: boolean;
  onChanged: () => void;
}) {
  const { descriptor, state } = listing;
  const environmentId = usePrimaryEnvironmentId();
  const activatePlugin = useAtomCommand(
    serverEnvironment.mcpGatewayActivatePlugin,
    "mcp gateway activate plugin",
  );
  const deactivatePlugin = useAtomCommand(
    serverEnvironment.mcpGatewayDeactivatePlugin,
    "mcp gateway deactivate plugin",
  );
  const startOAuth = useAtomCommand(
    serverEnvironment.mcpGatewayStartOAuth,
    "mcp gateway start oauth",
  );
  const [credentialDrafts, setCredentialDrafts] = useState<Record<string, string>>({});
  const [configDrafts, setConfigDrafts] = useState<Record<string, string>>({});
  const [rowState, setRowState] = useState<
    { readonly status: "idle" | "working" } | { readonly status: "error"; readonly message: string }
  >({ status: "idle" });
  const [connectingSince, setConnectingSince] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [showManualForm, setShowManualForm] = useState(descriptor.authMode === "secrets");

  const isOAuth = descriptor.authMode === "oauth-remote";
  const connectedViaOAuth = state.credentialSource === "oauth";

  // While a consent flow is open in the browser, poll the plugin list until
  // it flips to active (success) or error, or the flow TTL elapses.
  useEffect(() => {
    if (connectingSince === null) return;
    if (state.status === "active" || state.status === "error") {
      setConnectingSince(null);
      return;
    }
    if (Date.now() - connectingSince > OAUTH_POLL_TIMEOUT_MS) {
      setConnectingSince(null);
      setRowState({ status: "error", message: "Timed out waiting for authorization." });
      return;
    }
    const timer = setInterval(onChanged, OAUTH_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [connectingSince, onChanged, state.status]);

  const connect = useCallback(() => {
    if (environmentId === null) return;
    setRowState({ status: "working" });
    void (async () => {
      const result = await startOAuth({
        environmentId,
        input: { pluginId: descriptor.pluginId },
      });
      if (result._tag === "Success") {
        const api = readLocalApi();
        if (api) {
          void api.shell.openExternal(result.value.authorizationUrl).catch(() => {
            window.open(result.value.authorizationUrl, "_blank", "noopener,noreferrer");
          });
        } else {
          window.open(result.value.authorizationUrl, "_blank", "noopener,noreferrer");
        }
        setRowState({ status: "idle" });
        setConnectingSince(Date.now());
      } else if (!isAtomCommandInterrupted(result)) {
        const error = squashAtomCommandFailure(result);
        setRowState({
          status: "error",
          message: error instanceof Error ? error.message : "Starting the connection failed.",
        });
      } else {
        setRowState({ status: "idle" });
      }
    })();
  }, [descriptor.pluginId, environmentId, startOAuth]);

  const configValue = useCallback(
    (key: string) => configDrafts[key] ?? state.configValues[key] ?? "",
    [configDrafts, state.configValues],
  );

  const runCommand = useCallback(
    async (run: () => Promise<AtomCommandResult<unknown, unknown>>) => {
      setRowState({ status: "working" });
      const result = await run();
      if (result._tag === "Success" || isAtomCommandInterrupted(result)) {
        setCredentialDrafts({});
        setConfigDrafts({});
        setRowState({ status: "idle" });
      } else {
        const error = squashAtomCommandFailure(result);
        setRowState({
          status: "error",
          message: error instanceof Error ? error.message : "The gateway update failed.",
        });
      }
      onChanged();
    },
    [onChanged],
  );

  const activate = useCallback(() => {
    if (environmentId === null) return;
    const credentials = Object.fromEntries(
      Object.entries(credentialDrafts).filter(([, value]) => value.trim().length > 0),
    );
    const config = Object.fromEntries(
      descriptor.configFields.map((field) => [field.key, configValue(field.key)]),
    );
    void runCommand(() =>
      activatePlugin({
        environmentId,
        input: { pluginId: descriptor.pluginId, credentials, config },
      }),
    );
  }, [activatePlugin, configValue, credentialDrafts, descriptor, environmentId, runCommand]);

  const deactivate = useCallback(
    (forgetCredentials: boolean) => {
      if (environmentId === null) return;
      void runCommand(() =>
        deactivatePlugin({
          environmentId,
          input: { pluginId: descriptor.pluginId, forgetCredentials },
        }),
      );
    },
    [deactivatePlugin, descriptor.pluginId, environmentId, runCommand],
  );

  // Compact summary for the collapsed header; the expanded panel carries the
  // full message (error details, consent hint) via `statusDetail`.
  const headerBadge = useMemo(() => {
    if (rowState.status === "error" || state.status === "error") {
      return (
        <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-destructive">
          <XCircleIcon className="size-3" />
          Error
        </span>
      );
    }
    if (connectingSince !== null || state.status === "awaiting-consent") {
      return (
        <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
          <CircleIcon className="size-3 animate-pulse" />
          Waiting…
        </span>
      );
    }
    if (state.enabled) {
      return (
        <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-emerald-600">
          <CheckCircle2Icon className="size-3" />
          Connected
        </span>
      );
    }
    if (state.hasCredentials) {
      return (
        <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
          <CircleIcon className="size-3" />
          Off
        </span>
      );
    }
    return <span className="shrink-0 text-[11px] text-muted-foreground/60">Not connected</span>;
  }, [connectingSince, rowState.status, state.enabled, state.hasCredentials, state.status]);

  const statusDetail = useMemo(() => {
    if (rowState.status === "error") {
      return (
        <p className="inline-flex items-center gap-1 text-[11px] text-destructive">
          <XCircleIcon className="size-3 shrink-0" />
          {rowState.message}
        </p>
      );
    }
    if (state.status === "error") {
      return (
        <p className="inline-flex items-center gap-1 text-[11px] text-destructive">
          <XCircleIcon className="size-3 shrink-0" />
          {state.detail ?? "Connection error — reconnect."}
        </p>
      );
    }
    if (connectingSince !== null || state.status === "awaiting-consent") {
      return (
        <p className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <CircleIcon className="size-3 shrink-0 animate-pulse" />
          Waiting for authorization in your browser…
        </p>
      );
    }
    if (state.enabled) {
      return (
        <p className="inline-flex items-center gap-1 text-[11px] text-emerald-600">
          <CheckCircle2Icon className="size-3 shrink-0" />
          {connectedViaOAuth ? "Active — connected via OAuth" : "Active"}
        </p>
      );
    }
    if (state.hasCredentials) {
      return (
        <p className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <CircleIcon className="size-3 shrink-0" />
          Off — credentials saved, flip the switch to re-enable.
        </p>
      );
    }
    return null;
  }, [connectedViaOAuth, connectingSince, rowState, state]);

  return (
    <div className="border-t border-border/60 first:border-t-0">
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <div className="flex items-center gap-3 px-4 sm:px-5">
          <CollapsibleTrigger
            className="flex min-w-0 flex-1 items-center gap-3 py-3.5 text-left data-panel-open:[&>svg]:rotate-180"
            aria-label={`${descriptor.displayName} connection options`}
          >
            <span
              className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-background"
              aria-hidden
            >
              <IntegrationIcon pluginId={descriptor.pluginId} className="size-4.5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold tracking-[-0.01em] text-foreground">
                {descriptor.displayName}
              </span>
              <span className="block truncate text-xs text-muted-foreground/80">
                {descriptor.description}
              </span>
            </span>
            {headerBadge}
            <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform" />
          </CollapsibleTrigger>
          <Switch
            checked={state.enabled}
            disabled={
              !gatewayConfigured ||
              rowState.status === "working" ||
              (!state.enabled && !state.hasCredentials)
            }
            onCheckedChange={(enabled) => (enabled ? activate() : deactivate(false))}
            aria-label={`Enable ${descriptor.displayName}`}
          />
        </div>
        <CollapsiblePanel>
          <div className="space-y-3 px-4 pb-4 sm:px-5">
            <div className="space-y-1.5 border-t border-border/60 pt-3">
              <p className="text-xs text-muted-foreground/80">
                <a
                  className="underline decoration-border underline-offset-2 hover:text-foreground"
                  href={descriptor.docsUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Get credentials
                </a>
              </p>
              {statusDetail}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {isOAuth ? (
                <Button
                  size="xs"
                  disabled={
                    !gatewayConfigured || rowState.status === "working" || connectingSince !== null
                  }
                  onClick={connect}
                >
                  {connectingSince !== null
                    ? "Waiting…"
                    : state.status === "error" || connectedViaOAuth
                      ? "Reconnect"
                      : `Connect ${descriptor.displayName}`}
                </Button>
              ) : null}
              {state.hasCredentials ? (
                <Button
                  size="xs"
                  variant="ghost"
                  className="text-muted-foreground hover:text-destructive"
                  disabled={rowState.status === "working"}
                  onClick={() => deactivate(true)}
                >
                  {connectedViaOAuth ? "Disconnect" : "Forget credentials"}
                </Button>
              ) : null}
              {isOAuth ? (
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline decoration-border underline-offset-2 hover:text-foreground"
                  onClick={() => setShowManualForm((visible) => !visible)}
                >
                  {showManualForm ? "Hide manual setup" : "Use an API token instead"}
                </button>
              ) : null}
            </div>
            {showManualForm ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {descriptor.credentialFields.map((field) => (
                  <FieldInput
                    key={field.key}
                    field={field}
                    value={credentialDrafts[field.key] ?? ""}
                    {...(state.hasCredentials
                      ? { placeholderOverride: "Saved — enter to replace" }
                      : {})}
                    onChange={(value) =>
                      setCredentialDrafts((drafts) => ({ ...drafts, [field.key]: value }))
                    }
                  />
                ))}
                {descriptor.configFields.map((field) => (
                  <FieldInput
                    key={field.key}
                    field={field}
                    value={configValue(field.key)}
                    onChange={(value) =>
                      setConfigDrafts((drafts) => ({ ...drafts, [field.key]: value }))
                    }
                  />
                ))}
                <div className="sm:col-span-2">
                  <Button
                    size="xs"
                    variant="outline"
                    disabled={!gatewayConfigured || rowState.status === "working"}
                    onClick={activate}
                  >
                    {rowState.status === "working"
                      ? "Working"
                      : isOAuth
                        ? "Save token & activate"
                        : state.enabled
                          ? "Update"
                          : "Activate"}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </CollapsiblePanel>
      </Collapsible>
    </div>
  );
}

export function IntegrationsSettings() {
  const environmentId = usePrimaryEnvironmentId();
  const statusQuery = useEnvironmentQuery(
    environmentId === null
      ? null
      : serverEnvironment.mcpGatewayStatus({ environmentId, input: {} }),
  );
  const pluginsQuery = useEnvironmentQuery(
    environmentId === null
      ? null
      : serverEnvironment.mcpGatewayPlugins({ environmentId, input: {} }),
  );
  const status = statusQuery.data;
  const refreshAll = useCallback(() => {
    statusQuery.refresh();
    pluginsQuery.refresh();
  }, [pluginsQuery, statusQuery]);

  return (
    <SettingsPageContainer>
      <GatewayConnectionSection status={status} onChanged={refreshAll} />
      <SettingsSection title="Apps">
        {pluginsQuery.data === null ? (
          <SettingsRow
            title="Loading plugins"
            description={
              pluginsQuery.error ?? "Fetching the available integrations from the server."
            }
          />
        ) : (
          pluginsQuery.data.map((listing) => (
            <PluginRow
              key={listing.descriptor.pluginId}
              listing={listing}
              gatewayConfigured={status?.configured === true}
              onChanged={refreshAll}
            />
          ))
        )}
      </SettingsSection>
    </SettingsPageContainer>
  );
}
